import { polarPoint, normDeg } from '../../VueQuestion/interactifs/SharedProtractor';
import { useLargeurBloc } from './largeurBloc';

/**
 * Scènes statiques pour l'impression des outils interactifs de géométrie.
 *
 * Sur papier, l'élève manipule son VRAI compas/rapporteur : on n'imprime que
 * la scène de base (figures, points, demi-droites), jamais l'outil ni son
 * interactivité. Même esprit que renderNumberLine : géométrie pure, zéro state.
 *
 * - PrintCompasScene      ← elements_additionnels `type: 'compas_interactif'`
 *   (config de CompasInteractifElementEleve : viewBox, segments, reportAxes,
 *   figureLabels, pointLabels?)
 * - PrintRapporteurScene  ← `type: 'rapporteur_interactif'`
 *   (config de RapporteurInteractifElementEleve : viewBox, vertex, ray1, ray2,
 *   rayLength, showHintArc — polarPoint importé pour la même convention d'angle)
 */

const LINE_COLOR = '#334155';   // ink-700
const TEXT_COLOR = '#1e293b';   // ink-800
const FONT = "'Plus Jakarta Sans', sans-serif";

/** STANDARD PAPIER : le viewBox dépend de la scène (coordonnées des
 *  segments), on ne le rétrécit pas ; en demi-colonne les textes sont
 *  agrandis d'un facteur 1,5 pour rester lisibles (≈ 10 px physiques). */
const FACTEUR_DEMI = 1.5;
const useFacteurTexte = () => (useLargeurBloc() === 'half' ? FACTEUR_DEMI : 1);

const PrintCompasScene = ({
    viewBox,
    segments = [],
    reportAxes = [],
    figureLabels = [],
    pointLabels = null,
}) => {
    const k = useFacteurTexte();
    const { w: W = 500, h: H = 220 } = viewBox || {};
    if (!segments.length && !reportAxes.length) return null;

    // Points dédupliqués — même logique que le composant élève : pointLabels
    // (placement extérieur pré-calculé) prioritaire, sinon extrémités des segments.
    let points;
    if (pointLabels && pointLabels.length > 0) {
        points = pointLabels;
    } else {
        const seen = new Set();
        points = [];
        segments.forEach((s) => {
            if (!seen.has(s.p1)) { seen.add(s.p1); points.push({ name: s.p1, label: s.p1Label || s.p1, x: s.x1, y: s.y1 }); }
            if (!seen.has(s.p2)) { seen.add(s.p2); points.push({ name: s.p2, label: s.p2Label || s.p2, x: s.x2, y: s.y2 }); }
        });
    }

    return (
        <svg viewBox={`0 0 ${W} ${H}`} className="ep-figure" preserveAspectRatio="xMidYMid meet" style={{ '--ep-svg-w': `${W}px` }}>
            {/* Côtés des figures */}
            {segments.map((s, i) => (
                <line key={`seg-${i}`} x1={s.x1} y1={s.y1} x2={s.x2} y2={s.y2} stroke={LINE_COLOR} strokeWidth={1.4} />
            ))}

            {/* Demi-droites de report (origine marquée + flèche + label) */}
            {reportAxes.map((a) => (
                <g key={`axe-${a.id}`}>
                    <line x1={a.x1} y1={a.y} x2={a.x2} y2={a.y} stroke={LINE_COLOR} strokeWidth={1.2} />
                    <polygon
                        points={`${a.x2 + 9},${a.y} ${a.x2},${a.y - 4} ${a.x2},${a.y + 4}`}
                        fill={LINE_COLOR}
                    />
                    <line x1={a.x1} y1={a.y - 5} x2={a.x1} y2={a.y + 5} stroke={LINE_COLOR} strokeWidth={1.2} />
                    {a.label && (
                        <text x={a.x1} y={a.y + 18} fontSize={11 * k} fill={TEXT_COLOR} fontFamily={FONT}>
                            {a.label}
                        </text>
                    )}
                </g>
            ))}

            {/* Labels "Figure 1 / Figure 2" */}
            {figureLabels.map((f, i) => (
                <text key={`fig-${i}`} x={f.cx} y={f.cy} textAnchor="middle" fontSize={12 * k} fontStyle="italic" fill={TEXT_COLOR} fontFamily={FONT}>
                    {f.text}
                </text>
            ))}

            {/* Sommets + étiquettes */}
            {points.map((p) => (
                <g key={`pt-${p.name}`}>
                    <circle cx={p.x} cy={p.y} r={2.4} fill={TEXT_COLOR} />
                    <text
                        x={p.labelX != null ? p.labelX : p.x}
                        y={p.labelY != null ? p.labelY : p.y - 8}
                        textAnchor="middle"
                        fontSize={12 * k}
                        fontWeight="600"
                        fill={TEXT_COLOR}
                        fontFamily={FONT}
                    >
                        {p.label}
                    </text>
                </g>
            ))}
        </svg>
    );
};

export const PrintRapporteurScene = ({
    viewBox = { w: 620, h: 420 },
    vertex,
    vertexLabel,
    ray1,
    ray2,
    rayLength = 200,
    showHintArc = true,
}) => {
    const k = useFacteurTexte();
    if (!vertex || !ray1 || !ray2) return null;
    const { w: W, h: H } = viewBox;

    const end1 = polarPoint(vertex.x, vertex.y, rayLength, ray1.angleDeg);
    const end2 = polarPoint(vertex.x, vertex.y, rayLength, ray2.angleDeg);

    // Arc "?" entre les deux côtés, le long du plus petit secteur
    let arc = null;
    if (showHintArc) {
        const R_ARC = 32;
        let a1 = ray1.angleDeg;
        let a2 = ray2.angleDeg;
        if (normDeg(a2 - a1) > 180) [a1, a2] = [a2, a1];
        const start = polarPoint(vertex.x, vertex.y, R_ARC, a1);
        const fin = polarPoint(vertex.x, vertex.y, R_ARC, a2);
        const mid = polarPoint(vertex.x, vertex.y, R_ARC + 16, a1 + normDeg(a2 - a1) / 2);
        arc = (
            <g>
                <path
                    d={`M ${start.x} ${start.y} A ${R_ARC} ${R_ARC} 0 0 0 ${fin.x} ${fin.y}`}
                    fill="none"
                    stroke={LINE_COLOR}
                    strokeWidth={1}
                    strokeDasharray="3 3"
                />
                <text x={mid.x} y={mid.y} textAnchor="middle" fontSize={13 * k} fontWeight="700" fill={TEXT_COLOR} fontFamily={FONT}>
                    ?
                </text>
            </g>
        );
    }

    return (
        <svg viewBox={`0 0 ${W} ${H}`} className="ep-figure" preserveAspectRatio="xMidYMid meet" style={{ '--ep-svg-w': `${W}px` }}>
            <line x1={vertex.x} y1={vertex.y} x2={end1.x} y2={end1.y} stroke={LINE_COLOR} strokeWidth={1.4} />
            <line x1={vertex.x} y1={vertex.y} x2={end2.x} y2={end2.y} stroke={LINE_COLOR} strokeWidth={1.4} />
            {arc}
            <circle cx={vertex.x} cy={vertex.y} r={2.4} fill={TEXT_COLOR} />
            {vertexLabel && (
                <text x={vertex.x - 10} y={vertex.y + 14} fontSize={12 * k} fontWeight="600" fill={TEXT_COLOR} fontFamily={FONT}>
                    {vertexLabel}
                </text>
            )}
            {ray1.labelEnd && (
                <text x={end1.x} y={end1.y - 6} textAnchor="middle" fontSize={12 * k} fontWeight="600" fill={TEXT_COLOR} fontFamily={FONT}>
                    {ray1.labelEnd}
                </text>
            )}
            {ray2.labelEnd && (
                <text x={end2.x} y={end2.y - 6} textAnchor="middle" fontSize={12 * k} fontWeight="600" fill={TEXT_COLOR} fontFamily={FONT}>
                    {ray2.labelEnd}
                </text>
            )}
        </svg>
    );
};

export default PrintCompasScene;
